import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Router from 'next/router';
import CampaignContext from './CampaignContext';
import useCampaignCheckout from './useCampaignCheckout';
import createConsent from '../consent';
import { getPluginConfig } from '../config';
import { MAX_TIMEOUT_MS, STORAGE_KEY } from '../constants';
import { buildRecord, pickRecord } from '../helpers/attribution';
import { extractCampaignParams, getSearchFromUrl } from '../helpers/params';
import {
  clearStoredAttribution,
  readStoredAttribution,
  writeStoredAttribution
} from '../helpers/storage';

// Captures campaign parameters from the arriving url and holds them for the
// journey. Values always live in memory; they are written to localStorage (so
// they survive reloads and reach other tabs) only while the configured consent
// strategy grants it, and removed from storage as soon as it does not. The set
// is sent with the checkout and cleared once the order is placed.
const CampaignProvider = ({ children }) => {
  const [record, setRecord] = useState(null);
  const recordRef = useRef(null);
  const consentRef = useRef(false);

  const update = useCallback(next => {
    recordRef.current = next;
    setRecord(next);
  }, []);

  const capture = useCallback(
    search => {
      const { attribution, expiryDays } = getPluginConfig();
      const params = extractCampaignParams(search);
      if (!params) return;

      const next = pickRecord({
        candidates: [recordRef.current, buildRecord({ params, expiryDays })],
        attribution
      });
      if (next === recordRef.current) return;

      update(next);
      if (consentRef.current) writeStoredAttribution(next);
    },
    [update]
  );

  const applyConsent = useCallback(
    granted => {
      consentRef.current = granted;
      if (!granted) {
        clearStoredAttribution();
        return;
      }

      // Reconcile what this page captured before consent was known with what is
      // stored. Either can be the newer (another tab may have captured since),
      // so pickRecord orders them by capture time.
      const { attribution } = getPluginConfig();
      const next = pickRecord({
        candidates: [recordRef.current, readStoredAttribution()],
        attribution
      });
      update(next);
      if (next) writeStoredAttribution(next);
    },
    [update]
  );

  const clear = useCallback(() => {
    clearStoredAttribution();
    update(null);
  }, [update]);

  useEffect(() => {
    let active = true;
    const consent = createConsent(getPluginConfig().consent);
    const onConsent = granted => {
      if (active) applyConsent(granted);
    };

    capture(window.location.search);
    consent
      .hasConsent()
      .then(onConsent)
      .catch(() => onConsent(false));
    const unsubscribe = consent.subscribe(onConsent);

    // Another tab captured, replaced or cleared the stored set.
    const onStorage = event => {
      if (event.key !== STORAGE_KEY || !consentRef.current) return;
      update(readStoredAttribution());
    };
    // A client side navigation to a campaign url within the site.
    const onRouteChange = url => capture(getSearchFromUrl(url));

    window.addEventListener('storage', onStorage);
    Router.events.on('routeChangeComplete', onRouteChange);

    return () => {
      active = false;
      unsubscribe();
      window.removeEventListener('storage', onStorage);
      Router.events.off('routeChangeComplete', onRouteChange);
    };
  }, [applyConsent, capture, update]);

  // The live set stops applying at its expiry even if this tab stays open; fall
  // back to whatever valid set storage holds (none if it was the same one).
  useEffect(() => {
    if (!record) return undefined;
    const delay = record.expiresAt - Date.now();
    const expire = () => update(consentRef.current ? readStoredAttribution() : null);
    if (delay <= 0) {
      expire();
      return undefined;
    }
    // Beyond the timer limit (about 24.8 days) the checkout's own expiry check
    // still applies, and any new capture drops the expired set.
    if (delay > MAX_TIMEOUT_MS) return undefined;
    const timer = setTimeout(expire, delay);
    return () => clearTimeout(timer);
  }, [record, update]);

  const value = useMemo(
    () => ({
      params: record ? record.params : {},
      capturedAt: record ? record.capturedAt : null,
      expiresAt: record ? record.expiresAt : null,
      hasAttribution: !!record,
      clear
    }),
    [record, clear]
  );

  useCampaignCheckout(value);

  return <CampaignContext.Provider value={value}>{children}</CampaignContext.Provider>;
};

CampaignProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export default CampaignProvider;
